import React, { useCallback } from 'react'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Button } from '../../ui/button'
import { CreateChroniqueurResponse } from '../../../core/interface/chronique.interface'
import GetMailFromPreselectedInfluencer from '../../../core/api/services/bookmetrie/private-reports/chroniqueur/get-mail-from-preselected-influencer.service'
import { BookInterface } from '../../../core/interface/book.interface'
import { buildRelativePath, PathConfig } from '../../../core/config/PathConfig'
import RecapChroniqueur from '../shared/RecapChroniqueur'
import { TableViewTypeInfluencerEnum } from '../../../core/enums/table-view-type-influencer.enum'
import DownloadCsvUtil from '../../../core/utils/downloadCsv.util'
import { useAppDispatch } from '../../../store/hooks'
import { setShowSnackBar } from '../../../store/reducers/web-app.reducer'
import ChroniquerProgressBar from '../shared/ChroniquerProgressBar'

interface AudiencePreValidatedSummaryProps {
  selectedUsersSummary: CreateChroniqueurResponse
  bookSelected: BookInterface[]
}

const AudiencePreValidatedSummary: React.FC<
  AudiencePreValidatedSummaryProps
> = ({ selectedUsersSummary, bookSelected }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const navigate = useNavigate()

  const handleFinalGenerate = useCallback(() => {
    GetMailFromPreselectedInfluencer(bookSelected[0].id_ean.toString() || '')
      .then((response: any) => {
        if (response && response.data) {
          DownloadCsvUtil(
            response.data,
            `emails-chroniqueur-${bookSelected[0].id_ean || 'export'}.csv`,
          )
        }

        navigate(
          buildRelativePath([
            PathConfig.BOOKMETRIE,
            PathConfig.PRIVATE_REPORTS,
            PathConfig.CHRONIQUEUR,
            selectedUsersSummary.report_id,
          ]),
          { replace: true },
        )
      })
      .catch(() => {
        dispatch(setShowSnackBar(t('snackbarMessage.errorMessage')))
      })
  }, [bookSelected])
  if (!selectedUsersSummary) return null

  return (
    <div className="h-full w-full bg-gray-50 px-8 py-4">
      {/* Header Banner */}
      <RecapChroniqueur
        viewType={TableViewTypeInfluencerEnum.NEW_CHRONIQUEUR_VIEW}
        selectedUsersCount={selectedUsersSummary.total}
        bookSelected={bookSelected}
      />
      <ChroniquerProgressBar currentStep={2} completedSteps={[1]} />

      {/* Main Content */}
      <div className="flex items-center justify-center flex-col w-full h-full px-8">
        <div className="flex flex-col items-start ">
          <h2 className="text-2xl font-semibold mb-8 text-[#252525]">
            Vous avez sélectionné {selectedUsersSummary.total} utilisateurs
          </h2>

          <div className="space-y-3 !text-left  mb-12 ">
            {(selectedUsersSummary.no_response ?? 0) > 0 && (
              <div className="text-[#525252] text-base">
                {selectedUsersSummary.no_response ?? 0} sans réponse
              </div>
            )}
            {(selectedUsersSummary.not_contacted ?? 0) > 0 && (
              <div className="text-[#525252] text-base">
                {selectedUsersSummary.not_contacted ?? 0} jamais sollicités
              </div>
            )}
            {(selectedUsersSummary.super_influencer ?? 0) > 0 && (
              <div className="text-[#525252] text-base">
                {selectedUsersSummary.super_influencer ?? 0} super chroniqueurs
              </div>
            )}
            {(selectedUsersSummary.influencer ?? 0) > 0 && (
              <div className="text-[#525252] text-base">
                {selectedUsersSummary.influencer ?? 0} chroniqueurs
              </div>
            )}
            {(selectedUsersSummary.interested ?? 0) > 0 && (
              <div className="text-[#525252] text-base">
                {selectedUsersSummary.interested ?? 0} intéressés
              </div>
            )}
          </div>

          <div className="text-center text-[#737373] mb-12 text-base">
            Télécharger les utilisateurs et générer votre chronique.
          </div>

          <div className="flex justify-center items-center w-full">
            <Button
              onClick={handleFinalGenerate}
              className="bg-[#252525] hover:bg-[#1a1a1a] text-white px-8 py-3 rounded-md flex items-center gap-2 text-base font-medium"
            >
              <Check className="h-4 w-4" />
              Générer
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AudiencePreValidatedSummary
